import { config } from 'src/utils/config';
import { getStandardHeaders, performFetchWithLogging } from '../utils/helpers';
import { dateDiffInDays, dateToUnixTimestamp } from 'src/utils/common';

export async function getStats(
  accessToken: string,
  start_time: string | undefined | null,
  end_time: string | undefined | null,
  user_id: number | null,
  stat_type: string | null,
  timeRange: string,
  cdn_name: string | null,
) {
  const queryParams = new URLSearchParams();

  if (user_id) {
    queryParams.append('user_id', user_id.toString());
  }

  if (timeRange === 'Past 24 Hours') {
    queryParams.append('period', '24');
  } else if (timeRange === 'Past 1 Week') {
    queryParams.append('period', '168');
  } else if (timeRange === 'Past 1 Month') {
    queryParams.append('period', '720');
  } else if (timeRange === 'Past 1 Year') {
    queryParams.append('period', '8760');
  } else if (start_time && end_time) {
    const response: any = await getTimeSeriesStats(
      accessToken,
      start_time,
      end_time,
      user_id,
      stat_type,
      stat_type === 'cdn' ? 'traffic' : '',
      timeRange,
    );

    const dataKey = Object.keys(response?.data)[0];
    const dataArray = response?.data?.[dataKey] || [];

    const totalSum = stat_type==='storage'? dataArray[dataArray.length - 1]?.value || 0 : dataArray.reduce((sum: number, item: any) => sum + (item.value || 0), 0);

    const transformedResponse: Record<string, any> = {
      usergroup_id: response.usergroup_id,
      data:
        stat_type === 'cdn'
          ? { sum: totalSum } 
          : { [dataKey]: { sum: totalSum } }, 
      period: Math.floor(Math.random() * 10000),
    };

    if (stat_type === 'cdn' && cdn_name?.trim()) {
      transformedResponse['cdn'] = cdn_name;
    }

    // Propagate CDN stats provenance (source: cloudflare | cdn77 | both, plus
    // the per-collector `sources` split) and live flag (false = data through
    // yesterday) so the CDN Usage card can decide visibility + labelling.
    if (stat_type === 'cdn') {
      if (response?.source) {
        transformedResponse['source'] = response.source;
      }
      if (response?.sources) {
        transformedResponse['sources'] = response.sources;
      }
      if (typeof response?.live === 'boolean') {
        transformedResponse['live'] = response.live;
      }
    }

    return transformedResponse;
  } else {
    queryParams.append('period', '24');
  }

  const url = `${config.hostUrl}/api/v1/stats/${stat_type}?${queryParams}`;

  try {
    return await performFetchWithLogging(
      url,
      {
        method: 'GET',
        headers: { ...getStandardHeaders(accessToken) },
        credentials: 'include',
      },
      'json',
    );
  } catch (error) {
    //console.error('getTimeSeriesStats error:', error);
    throw error;
  }
}

export async function getTimeSeriesStats(
  accessToken: string,
  start_time: string | undefined,
  end_time: string | undefined,
  user_id: number | null,
  stat_type: string | null,
  stat_subtype: string | '',
  timeRange: string,
) {
  const queryParams = new URLSearchParams();

  if (user_id) {
    queryParams.append('user_id', user_id.toString());
  }

  if (stat_subtype !== '') {
    queryParams.append('stat_subtype', stat_subtype);
  }

  // Handle undefined dates - fallback to Past 1 Week
  let dateStart: string;
  let dateEnd: string;
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];

  // If start_time or end_time is undefined, fallback to Past 1 Week
  if (!start_time || !end_time) {
    console.warn('start_time or end_time is undefined, falling back to Past 1 Week');
    
    // Set end date to today
    dateEnd = todayStr;
    
    // Set start date to 7 days ago
    const weekAgo = new Date(now);
    weekAgo.setDate(weekAgo.getDate() - 7);
    dateStart = weekAgo.toISOString().split('T')[0];
    
    // Override timeRange to ensure consistent behavior
    timeRange = 'Past 1 Week';
  } else if (!timeRange || timeRange === '') {
    // custom range: use exactly what the user passed in
    dateStart = start_time.split('T')[0];
    dateEnd = end_time.split('T')[0];
  } else {
    // rolling windows always end on "today"
    dateEnd = todayStr;

    if (timeRange === 'Past 1 Week') {
      const d = new Date(now);
      d.setDate(d.getDate() - 7);
      dateStart = d.toISOString().split('T')[0];
    } else if (timeRange === 'Past 1 Month') {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 1);
      dateStart = d.toISOString().split('T')[0];
    } else if (timeRange === 'Past 1 Year') {
      const d = new Date(now);
      d.setFullYear(d.getFullYear() - 1);
      d.setDate(d.getDate() + 1);
      dateStart = d.toISOString().split('T')[0];
    } else {
      // unexpected timeRange — fall back to user's exact inputs
      dateStart = start_time.split('T')[0];
      dateEnd = end_time.split('T')[0];
    }
  }

  // Additional validation - ensure dates are valid
  if (!dateStart || !dateEnd) {
    //console.error('Failed to determine valid start and end dates');
    throw new Error('Invalid date range');
  }

  // Validate that start date is not after end date
  if (new Date(dateStart) > new Date(dateEnd)) {
    //console.error('Start date cannot be after end date');
    throw new Error('Start date cannot be after end date');
  }

  // convert dateStart to UNIX at midnight
  const startTs = dateToUnixTimestamp(dateStart);

  let endTs: number;
  if (dateEnd === todayStr) {
    // Subtract 10 minutes from current time to avoid "Invalid end_time" errors
    // on servers that reject end_time values that are too close to or in the future
    const tenMinutesAgo = Math.floor(Date.now() / 1000) - (10 * 60);
    endTs = tenMinutesAgo;
  } else {
    endTs = dateToUnixTimestamp(`${dateEnd} 23:59:59`);
  }

  queryParams.append('start_time', startTs.toString());
  queryParams.append('end_time', endTs.toString());

  const url = `${config.hostUrl}/api/v1/stats/timeseries/${stat_type}?${queryParams}`;

  try {
    return await performFetchWithLogging(
      url,
      {
        method: 'GET',
        headers: { ...getStandardHeaders(accessToken) },
        credentials: 'include',
      },
      'json',
    );
  } catch (error) {
    //console.error('getTimeSeriesStats error:', error);
    throw error;
  }
}

export async function getStorageStats(
  accessToken: string,
  user_id: number | null = null,
) {
  const queryParams = new URLSearchParams();
  
  queryParams.append('period', '720');

  if (user_id) {
    queryParams.append('user_id', user_id.toString());
  }

  const url = `${config.hostUrl}/api/v1/stats/storage?${queryParams}`;

  try {
    return await performFetchWithLogging(
      url,
      {
        method: 'GET',
        headers: { ...getStandardHeaders(accessToken) },
        credentials: 'include',
      },
      'json',
    );
  } catch (error) {
    //console.error('getStorageStats error:', error);
    throw error;
  }
}

// Helper function to convert date string to UNIX timestamp
